import { AxiosError } from 'axios';
import { CardCheckout } from '../../features/checkout/types';

interface CustomError {
  message?: string;
  details?: string[];
}

const DEFAULT_CARD_ERROR_MESSAGE =
  'Não foi possível processar o pagamento com este cartão. Revise os dados e tente novamente.';

const normalizeCardApiMessage = (message?: string): string => {
  if (!message) {
    return DEFAULT_CARD_ERROR_MESSAGE;
  }

  const normalizedMessage = message.trim();
  const lowerCaseMessage = normalizedMessage.toLowerCase();

  if (
    lowerCaseMessage.includes('cvv') ||
    lowerCaseMessage.includes('security code') ||
    lowerCaseMessage.includes('codigo de seguranca') ||
    lowerCaseMessage.includes('código de segurança')
  ) {
    return 'O código de segurança do cartão está incorreto. Confira o CVV e tente novamente.';
  }

  if (
    lowerCaseMessage.includes('expiration') ||
    lowerCaseMessage.includes('expira') ||
    lowerCaseMessage.includes('validade') ||
    lowerCaseMessage.includes('expired')
  ) {
    return 'A validade do cartão parece incorreta. Revise os dados e tente novamente.';
  }

  if (
    lowerCaseMessage.includes('insufficient') ||
    lowerCaseMessage.includes('saldo') ||
    lowerCaseMessage.includes('limite') ||
    lowerCaseMessage.includes('funds')
  ) {
    return 'O pagamento não foi aprovado pelo banco. Verifique o limite do cartão ou tente outro.';
  }

  if (
    lowerCaseMessage.includes('3ds') ||
    lowerCaseMessage.includes('challenge') ||
    lowerCaseMessage.includes('authentication')
  ) {
    return 'Não foi possível confirmar a autenticação do cartão. Tente novamente ou use outro cartão.';
  }

  if (
    lowerCaseMessage.includes('installment') ||
    lowerCaseMessage.includes('parcel') ||
    lowerCaseMessage.includes('parcela')
  ) {
    return 'Selecione a quantidade de parcelas para continuar.';
  }

  if (
    lowerCaseMessage.includes('card') ||
    lowerCaseMessage.includes('cartao') ||
    lowerCaseMessage.includes('cartão')
  ) {
    return normalizedMessage;
  }

  return normalizedMessage;
};

export const getCardApiErrorMessages = (
  error: unknown,
  fallbackMessage = DEFAULT_CARD_ERROR_MESSAGE,
): string[] => {
  if (error instanceof AxiosError) {
    const axiosError = error as AxiosError<CustomError>;
    const details = axiosError.response?.data?.details?.filter(Boolean) ?? [];

    if (details.length > 0) {
      return details.map(detail => normalizeCardApiMessage(detail));
    }

    if (axiosError.response?.data?.message) {
      return [normalizeCardApiMessage(axiosError.response.data.message)];
    }
  }

  return [fallbackMessage];
};

export const getCardCheckoutErrorMessage = (
  cardCheckout?: Partial<CardCheckout>,
  fallbackMessage = 'Selecione um cartão para continuar.',
): string => {
  if (!cardCheckout) {
    return fallbackMessage;
  }

  const errors = [
    cardCheckout.securityCodeError,
    cardCheckout.installmentsError,
    cardCheckout.valueError,
    cardCheckout.numberError,
    cardCheckout.expDateError,
    cardCheckout.holderError,
    cardCheckout.documentError,
    cardCheckout.validationValueError,
  ].filter(Boolean);

  if (errors.length > 0) {
    return String(errors[0]);
  }

  return fallbackMessage;
};
